/**
 * Billing plan comparison rows, keyed by canonical plan code.
 *
 * The WP catalog only sends free-text highlights, so the table cannot line rows up from it.
 * Keep these values in sync with the catalog / entitlements until the catalog returns
 * structured limits (then build the rows from the API instead).
 */

/** true = included, false = not included, string = the tier's limit. */
export type PlanCompareValue = boolean | string;

export interface PlanCompareRow {
  label: string;
  values: Record<string, PlanCompareValue>;
}

export interface PlanCompareGroup {
  title: string;
  rows: PlanCompareRow[];
}

export const PLAN_COMPARE_GROUPS: PlanCompareGroup[] = [
  {
    title: 'Monitoring',
    rows: [
      {
        label: 'Live Wall (live screen of the team)',
        values: { basic: false, professional: true, business: true },
      },
      {
        label: 'Live screencast (watch one screen)',
        values: { basic: '45 min/day', professional: '90 min/day', business: '180 min/day' },
      },
      {
        label: 'Screenshot interval',
        values: { basic: '15 min', professional: '10 min', business: '5 min' },
      },
      { label: 'App-switch screenshots', values: { basic: false, professional: true, business: true } },
      {
        label: 'How tracking runs',
        values: {
          basic: 'Manual',
          professional: 'Manual, Automatic, Hidden',
          business: 'Manual, Automatic, Hidden',
        },
      },
    ],
  },
  {
    title: 'Data',
    rows: [
      {
        label: 'How long screenshots are kept',
        values: { basic: '15 days', professional: '30 days', business: '60 days' },
      },
      {
        label: 'Activity logs',
        values: { basic: '90 days', professional: '90 days', business: '180 days' },
      },
    ],
  },
  {
    title: 'Reports',
    rows: [
      { label: 'App & website usage', values: { basic: true, professional: true, business: true } },
      {
        label: 'Productivity Trends (apps & sites over time)',
        values: { basic: false, professional: true, business: true },
      },
      {
        label: 'Work Replay (screenshot timeline)',
        values: { basic: false, professional: true, business: true },
      },
      {
        label: 'Attendance & timesheet insight',
        values: { basic: true, professional: true, business: true },
      },
      { label: 'Executive reports', values: { basic: false, professional: false, business: true } },
    ],
  },
  {
    title: 'Teams & support',
    rows: [
      { label: 'Teams', values: { basic: 'Up to 2', professional: 'Up to 10', business: 'Unlimited' } },
      {
        label: 'Manager and team lead roles',
        values: { basic: false, professional: true, business: true },
      },
      { label: 'Priority setup help', values: { basic: false, professional: false, business: true } },
    ],
  },
];

export const PLAN_COMPARE_FOOTNOTE =
  'Webcam capture is an optional add-on on every plan. Introduce SuperSee openly — your team can see their own activity.';

/**
 * What the higher of two tiers has that the lower lacks, as short phrases
 * ("Work Replay", "Screenshot retention: 60 days instead of 30 days").
 * Used for "what you get" on upgrade and "what you lose" on downgrade.
 */
export function planCompareGains(lower: string, higher: string): string[] {
  const lines: string[] = [];
  for (const group of PLAN_COMPARE_GROUPS) {
    for (const row of group.rows) {
      const low = row.values[lower];
      const high = row.values[higher];
      if (low === undefined || high === undefined || low === high || high === false) {
        continue;
      }
      if (high === true || low === false) {
        lines.push(high === true ? row.label : `${row.label}: ${high}`);
      } else {
        lines.push(`${row.label}: ${high} instead of ${low}`);
      }
    }
  }
  return lines;
}
